import * as React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Link from '@mui/material/Link';

import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import { fetchAdSummaryByAd, fetchAdPreview } from '@src/api';
import api from '@src/api/clients';
import { useAuth } from '@src/context';
import { formatDateToYMD } from '@src/utils/dateUtils';

import {
  CLIENT_FIELDS,
  MAX_CLIENT_META_PAGES,
  arraysEqual,
  computeDerivedMetrics,
  getLastThirtyDays,
  safeDiv,
  safeNumber,
} from '../creative-performance/utils';

const VIEW_STORAGE_KEY = 'adPerformance.viewMode';
const METRICS_STORAGE_KEY = 'adPerformance.selectedMetrics';

const METRIC_OPTIONS = [
  { key: 'spend', label: 'Spend' },
  { key: 'leadsCpl', label: 'Leads + CPL' },
  { key: 'bookedCpb', label: 'Booked + Cost/Booked' },
  { key: 'issuesCpi', label: 'Issues + Cost/Issue' },
  { key: 'demosCpd', label: 'Demos + Cost/Demo' },
  { key: 'soldCps', label: 'Sold + Cost/Sale' },
  { key: 'revenueCom', label: 'Revenue + CoM' },
];

const DEFAULT_SELECTED_METRICS = ['spend', 'leadsCpl', 'bookedCpb'];

const PAGE_SIZE = 50;

// An ad younger than this is flagged "Maturing": its leads haven't had
// time to move through the booking/demo/sale funnel, so back-end metrics
// (Cost/Booked, CoM, etc.) read worse than they really are. The backend
// cohort lag is 7 days; we use 14 to also cover the demo→sale tail.
const MATURING_DAYS = 14;

// Whole days since an ad launched, or null if the date is missing/invalid.
const getAdAgeDays = (createdTime) => {
  if (!createdTime) return null;
  const created = new Date(createdTime);
  if (Number.isNaN(created.getTime())) return null;
  const ms = Date.now() - created.getTime();
  return ms < 0 ? 0 : Math.floor(ms / 86400000);
};

const formatLaunchDate = (createdTime) => {
  if (!createdTime) return '';
  const d = new Date(createdTime);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatAdAge = (ageDays) => {
  if (ageDays == null) return '';
  if (ageDays <= 0) return 'today';
  if (ageDays === 1) return '1d ago';
  return `${ageDays}d ago`;
};

const maturingReason = (ageDays) =>
  `Launched ${formatAdAge(ageDays)} — leads typically take 7–10 days to book and sell, `
  + 'so booking/demo/sale metrics are still maturing and will improve as the ad bakes.';

// How deep into the funnel an ad has accumulated enough volume to be judged
// fairly. Thresholds mirror the internal KPI_STAGE_RULES; labels are
// rewritten for a client audience (no "Issue Stage" / "Creative Signal"
// jargon). The chip tells a client *which* downstream metrics are
// statistically meaningful and which are still extrapolations.
const getMaturityStage = (row) => {
  const hasBackend = Boolean(row.hasBackendIntegration);
  if (hasBackend && row.netSold >= 3 && row.netAmount > 0) {
    return {
      key: 'sales',
      label: 'Sales-proven',
      tooltip: `${row.netSold} sales recorded — Cost of Marketing is backed by real revenue.`,
      bgcolor: 'success.main',
    };
  }
  if (hasBackend && row.demos >= 5) {
    return {
      key: 'demo',
      label: 'Demo-tested',
      tooltip: `${row.demos} demos recorded — Cost/Demo is meaningful. Sales still maturing.`,
      bgcolor: 'success.light',
    };
  }
  if (hasBackend && row.issues >= 5) {
    return {
      key: 'pipeline',
      label: 'Pipeline-tested',
      tooltip: `${row.issues} qualified leads in pipeline — funnel data is meaningful. Sales still maturing.`,
      bgcolor: 'success.light',
    };
  }
  if (hasBackend && row.scheduled >= 8) {
    return {
      key: 'appt',
      label: 'Appt-tested',
      tooltip: `${row.scheduled} appointments scheduled — cost-per-appointment is meaningful. Sales still maturing.`,
      bgcolor: 'info.main',
    };
  }
  if (row.leads >= 20) {
    return {
      key: 'lead',
      label: 'Lead-tested',
      tooltip: `${row.leads} leads in — CPL is meaningful. Bookings, demos and sales still working through the funnel.`,
      bgcolor: 'info.main',
    };
  }
  if (row.impressions >= 10000) {
    return {
      key: 'building',
      label: 'Building data',
      tooltip: `${row.impressions.toLocaleString()} impressions — too few leads yet to judge cost-per-lead. Spend/impressions are real; everything downstream is still forming.`,
      bgcolor: 'grey.500',
    };
  }
  return {
    key: 'early',
    label: 'Too early',
    tooltip: 'Not enough impressions, leads or sales yet to evaluate this ad. Costs will look high until volume builds.',
    bgcolor: 'grey.500',
  };
};

// Specifically about whether Cost of Marketing is trustworthy, since that's
// the metric clients fixate on. Returns null below 10 sales — at that point
// the Maturity chip already covers the story and a second "CoM not ready"
// chip would just be noise.
const getComConfidence = (row) => {
  if (!row.hasBackendIntegration) return null;
  const sales = row.netSold;
  if (sales >= 30) {
    return {
      key: 'stable',
      label: 'CoM: Stable',
      tooltip: `${sales} sales — Cost of Marketing has converged and is reliable for decision-making.`,
      bgcolor: 'success.main',
    };
  }
  if (sales >= 20) {
    return {
      key: 'trending',
      label: 'CoM: Trending',
      tooltip: `${sales} sales — Cost of Marketing direction is reliable, exact value still tightening.`,
      bgcolor: 'success.light',
    };
  }
  if (sales >= 10) {
    return {
      key: 'early',
      label: 'CoM: Early',
      tooltip: `${sales} sales — Cost of Marketing is an early read. Expect movement as more sales come in.`,
      bgcolor: 'info.main',
    };
  }
  return null;
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active only' },
  { value: 'all', label: 'Active + Paused' },
];

async function fetchAllClientMetadata() {
  const allClients = [];
  let page = 1;
  while (page <= MAX_CLIENT_META_PAGES) {
    const response = await api.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload: { page, page_size: 250, ordering: 'name', fields: CLIENT_FIELDS },
    });
    const data = response?.data;
    if (Array.isArray(data)) { allClients.push(...data); break; }
    const results = Array.isArray(data?.results) ? data.results : [];
    allClients.push(...results);
    if (!data?.next) break;
    page += 1;
  }
  return allClients;
}

const mapRow = (row = {}, clientMetaById) => {
  const clientId = row.client_id ? String(row.client_id) : '';
  const clientMeta = clientId ? clientMetaById.get(clientId) : null;
  const base = {
    uuid: row.uuid || `${row.ad_id || row.ad_name || ''}-${clientId}`,
    clientId,
    clientName: clientMeta?.name || row.client_name || row.client__name || '',
    adId: row.ad_id || '',
    adName: row.ad_name || row.ad || '',
    adSetName: row.ad_set_name || '',
    campaign: row.campaign || '',
    isActive: row.active == null ? true : Boolean(row.active),
    headline: row.headline || '',
    primaryText: row.primary_text || '',
    description: row.description || '',
    destinationUrl: row.destination_url || '',
    ctaType: row.cta_type || '',
    thumbnailUrl: row.thumbnail_url || '',
    imageUrl: row.image_url || '',
    videoId: row.video_id || '',
    facebookFormat: row.facebook_format || '',
    vertical: row.vertical || '',
    campaignType: row.campaign_type || '',
    createdTime: row.ad_created_time || '',
    // hasMapping on the client = backend (CRM) data flows in, so funnel
    // stages past the lead are observable. Without it the only thing we can
    // judge is impressions/CPL — the stage chip caps at Lead-tested.
    hasBackendIntegration: Boolean(clientMeta?.hasMapping),
    spend: safeNumber(row.total_spend_sum),
    impressions: safeNumber(row.impressions_sum),
    clicks: safeNumber(row.clicks_sum),
    landingPageViews: safeNumber(row.landing_page_views_sum),
    videoPlays3s: safeNumber(row.video_plays_3s_sum),
    thruplays: safeNumber(row.thruplays_sum),
    leads: safeNumber(row.total_leads_sum),
    selfBook: safeNumber(row.self_books_sum),
    booked: safeNumber(row.apps_booked_sum || row.booked_sum),
    scheduled: safeNumber(row.scheduled_sum),
    issues: safeNumber(row.issue_sum || row.issues_sum),
    demos: safeNumber(row.demo_sum || row.demos_sum),
    netSold: safeNumber(row.net_sold_sum || row.net_sales_sum),
    netAmount: safeNumber(row.net_total_sum || row.net_amount_sum),
  };
  const derived = computeDerivedMetrics(base);
  const cpb = safeDiv(base.spend, base.booked);
  const cpi = safeDiv(base.spend, base.issues);
  const cpd = safeDiv(base.spend, base.demos);
  const cps = safeDiv(base.spend, base.netSold);

  // Mature-cohort read: back-end metrics restricted to leads old enough
  // (7+ days, per the backend cohort lag) to have moved through the
  // booking/demo/sale funnel. computeDerivedMetrics already prefers these
  // mature_* inputs when present, so CoM/cost-per-X come out honest.
  const matureBase = {
    ...base,
    matureSpend: safeNumber(row.mature_spend_sum),
    matureLeads: safeNumber(row.mature_leads_sum),
    matureBooked: safeNumber(row.mature_booked_sum),
    matureScheduled: safeNumber(row.mature_scheduled_sum),
    matureIssues: safeNumber(row.mature_issues_sum),
    matureDemos: safeNumber(row.mature_demos_sum),
    matureGrossSold: safeNumber(row.mature_gross_sales_sum),
    matureNetSold: safeNumber(row.mature_net_sales_sum),
    matureNetAmount: safeNumber(row.mature_net_amount_sum),
  };
  const matureDerived = computeDerivedMetrics(matureBase);

  return {
    ...base,
    ...derived,
    costPerBooked: cpb,
    costPerIssue: cpi,
    costPerDemo: cpd,
    costPerSale: cps,
    // hasMatureData is false when no lead from this ad is old enough yet
    // (brand-new ad, or a date window shorter than the cohort lag).
    hasMatureData: matureBase.matureLeads > 0,
    matureLeads: matureBase.matureLeads,
    matureBooked: matureBase.matureBooked,
    matureIssues: matureBase.matureIssues,
    matureDemos: matureBase.matureDemos,
    matureNetSold: matureBase.matureNetSold,
    matureNetAmount: matureBase.matureNetAmount,
    matureCostOfMarketing: matureDerived.costOfMarketing,
    matureCostPerBooked: matureDerived.cpBooked,
    matureCostPerIssue: matureDerived.cpIssue,
    matureCostPerDemo: matureDerived.cpDemo,
    matureCostPerSale: safeDiv(matureBase.matureSpend, matureBase.matureNetSold),
  };
};

function ConfidenceChip({ chip, size = 'small', height }) {
  if (!chip) return null;
  return (
    <Tooltip title={chip.tooltip}>
      <Chip
        label={chip.label}
        size={size}
        sx={{
          fontWeight: 700,
          color: '#fff',
          bgcolor: chip.bgcolor,
          ...(height ? { height, fontSize: 11 } : {}),
        }}
      />
    </Tooltip>
  );
}

function StatusPill({ active }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        backgroundColor: active ? 'success.light' : 'grey.300',
        color: active ? 'success.dark' : 'text.secondary',
      }}
    >
      {active ? 'Active' : 'Paused'}
    </Box>
  );
}

function MetricCell({ label, value, sub, subLabel, dimmed, dimReason, note }) {
  const cell = (
    <Box sx={{ minWidth: 0, opacity: dimmed ? 0.4 : 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{label}</Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
      {sub != null ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
          {subLabel}: {sub}
        </Typography>
      ) : null}
      {note != null ? (
        <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2, color: 'text.disabled' }}>
          {note}
        </Typography>
      ) : null}
    </Box>
  );
  if (dimmed && dimReason) {
    return <Tooltip title={dimReason}>{cell}</Tooltip>;
  }
  return cell;
}

function renderMetricCells(row, selected, maturing) {
  // Spend and Leads/CPL are real the moment an ad runs. Everything
  // downstream of the lead (booked/issues/demos/sold/revenue) lags the
  // 7-10 day lead-to-sale cycle, so we dim those on a maturing ad.
  const dimReason = maturing ? maturingReason(getAdAgeDays(row.createdTime)) : '';
  const cells = [];
  if (selected.includes('spend')) {
    cells.push(<MetricCell key="spend" label="Spend" value={<DollarFormatter value={row.spend} />} />);
  }
  if (selected.includes('leadsCpl')) {
    cells.push(
      <MetricCell
        key="leadsCpl"
        label="Leads"
        value={row.leads.toLocaleString()}
        sub={row.cpl > 0 ? <DollarFormatter value={row.cpl} /> : '—'}
        subLabel="CPL"
      />
    );
  }
  if (selected.includes('bookedCpb')) {
    cells.push(
      <MetricCell
        key="bookedCpb"
        label="Booked"
        value={row.booked.toLocaleString()}
        sub={row.costPerBooked > 0 ? <DollarFormatter value={row.costPerBooked} /> : '—'}
        subLabel="Cost/Booked"
        dimmed={maturing}
        dimReason={dimReason}
      />
    );
  }
  if (selected.includes('issuesCpi')) {
    cells.push(
      <MetricCell
        key="issuesCpi"
        label="Issues"
        value={row.issues.toLocaleString()}
        sub={row.costPerIssue > 0 ? <DollarFormatter value={row.costPerIssue} /> : '—'}
        subLabel="Cost/Issue"
        dimmed={maturing}
        dimReason={dimReason}
      />
    );
  }
  if (selected.includes('demosCpd')) {
    cells.push(
      <MetricCell
        key="demosCpd"
        label="Demos"
        value={row.demos.toLocaleString()}
        sub={row.costPerDemo > 0 ? <DollarFormatter value={row.costPerDemo} /> : '—'}
        subLabel="Cost/Demo"
        dimmed={maturing}
        dimReason={dimReason}
      />
    );
  }
  if (selected.includes('soldCps')) {
    cells.push(
      <MetricCell
        key="soldCps"
        label="Sold"
        value={row.netSold.toLocaleString()}
        sub={row.costPerSale > 0 ? <DollarFormatter value={row.costPerSale} /> : '—'}
        subLabel="Cost/Sale"
        dimmed={maturing}
        dimReason={dimReason}
      />
    );
  }
  if (selected.includes('revenueCom')) {
    cells.push(
      <MetricCell
        key="revenueCom"
        label="Revenue"
        value={row.netAmount > 0 ? <DollarFormatter value={row.netAmount} /> : '—'}
        sub={row.netAmount > 0 ? <PercentageFormatter value={row.costOfMarketing} /> : '—'}
        subLabel="CoM"
        dimmed={maturing}
        dimReason={dimReason}
      />
    );
  }
  return cells;
}

const pickMedia = (row) => row.imageUrl || row.thumbnailUrl || '';

const isVideoRow = (row) =>
  row.facebookFormat === 'VIDEO' ||
  row.facebookFormat === 'DYNAMIC' ||
  Boolean(row.videoId);

// Monospace ad ID that copies to the clipboard on click. Stops click
// propagation so copying from a card doesn't also open the preview modal.
function CopyableAdId({ adId, sx }) {
  const [copied, setCopied] = React.useState(false);
  if (!adId) return null;
  const handleCopy = (event) => {
    event.stopPropagation();
    try {
      navigator.clipboard?.writeText(String(adId));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      // clipboard unavailable — ignore
    }
  };
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy ad ID'}>
      <Typography
        variant="caption"
        color="text.secondary"
        onClick={handleCopy}
        sx={{
          fontFamily: 'monospace',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          maxWidth: '100%',
          '&:hover': { color: 'text.primary' },
          ...sx,
        }}
      >
        <ContentCopyIcon sx={{ fontSize: 12, flexShrink: 0 }} />
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{adId}</Box>
      </Typography>
    </Tooltip>
  );
}

// Renders the stored creative poster. For the rare ad with no stored
// thumbnail, lazily fetches the live Facebook preview iframe (same source
// as the detail modal) once the tile scrolls into view. The fetch is
// bounded by pagination and only fires for missing-thumbnail tiles, so it
// stays cheap even under concurrent traffic.
function TilePreview({ row }) {
  const media = pickMedia(row);
  const containerRef = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  const [iframeHtml, setIframeHtml] = React.useState('');
  const [state, setState] = React.useState('idle'); // idle | loading | done | error

  React.useEffect(() => {
    if (media || inView) return undefined;
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [media, inView]);

  React.useEffect(() => {
    if (media || !inView || state !== 'idle' || !row.adId) return undefined;
    let cancelled = false;
    setState('loading');
    fetchAdPreview({ ad_id: row.adId, ad_format: 'MOBILE_FEED_STANDARD' })
      .then((response) => {
        if (cancelled) return;
        const html = response?.data?.iframe_html || '';
        setIframeHtml(html);
        setState(html ? 'done' : 'error');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [media, inView, state, row.adId]);

  if (media) {
    return (
      <Box
        component="img"
        src={media}
        alt={row.adName || 'Ad creative'}
        loading="lazy"
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }

  return (
    <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }}>
      {state === 'loading' ? (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : state === 'done' ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            '& iframe': { width: '100% !important', height: '100% !important', border: 0, display: 'block' },
          }}
          dangerouslySetInnerHTML={{ __html: iframeHtml }}
        />
      ) : (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" color="text.secondary">No preview image</Typography>
        </Box>
      )}
    </Box>
  );
}

function AdTile({ row, selectedMetrics, onClick }) {
  const paused = !row.isActive;
  const ageDays = getAdAgeDays(row.createdTime);
  const maturing = ageDays != null && ageDays < MATURING_DAYS;
  const maturityStage = getMaturityStage(row);
  const comConfidence = getComConfidence(row);
  return (
    <Card
      onClick={() => onClick(row)}
      sx={{
        cursor: 'pointer',
        opacity: paused ? 0.65 : 1,
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{ position: 'relative', pt: '100%', bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}>
        <TilePreview row={row} />
        {isVideoRow(row) ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PlayArrowIcon fontSize="large" />
            </Box>
          </Box>
        ) : null}
        <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
          <StatusPill active={!paused} />
        </Box>
        {maturing ? (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <Tooltip title={maturingReason(ageDays)}>
              <Chip
                label="🌱 Maturing"
                size="small"
                color="warning"
                sx={{ fontWeight: 700, bgcolor: 'warning.main', color: '#fff' }}
              />
            </Tooltip>
          </Box>
        ) : null}
      </Box>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box>
          {row.vertical ? (
            <Chip
              label={row.vertical}
              size="small"
              color="primary"
              sx={{ mb: 0.75, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}
            />
          ) : null}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {row.adName || '(no name)'}
          </Typography>
          {row.headline ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.headline}
            </Typography>
          ) : null}
          {row.clientName ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{row.clientName}</Typography>
          ) : null}
          <CopyableAdId adId={row.adId} sx={{ mt: 0.25 }} />
          {row.createdTime ? (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.25, color: maturing ? 'warning.main' : 'text.secondary', fontWeight: maturing ? 700 : 400 }}
            >
              Launched {formatLaunchDate(row.createdTime)} · {formatAdAge(ageDays)}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
            <ConfidenceChip chip={maturityStage} height={20} />
            <ConfidenceChip chip={comConfidence} height={20} />
          </Stack>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 1.25 }}>
          {renderMetricCells(row, selectedMetrics, maturing)}
        </Box>
      </CardContent>
    </Card>
  );
}

function AdTable({ rows, selectedMetrics, onRowClick }) {
  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 80 }}>Preview</TableCell>
            <TableCell>Ad</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Status</TableCell>
            {selectedMetrics.includes('spend') && <TableCell align="right">Spend</TableCell>}
            {selectedMetrics.includes('leadsCpl') && <TableCell align="right">Leads</TableCell>}
            {selectedMetrics.includes('leadsCpl') && <TableCell align="right">CPL</TableCell>}
            {selectedMetrics.includes('bookedCpb') && <TableCell align="right">Booked</TableCell>}
            {selectedMetrics.includes('bookedCpb') && <TableCell align="right">Cost/Booked</TableCell>}
            {selectedMetrics.includes('issuesCpi') && <TableCell align="right">Issues</TableCell>}
            {selectedMetrics.includes('issuesCpi') && <TableCell align="right">Cost/Issue</TableCell>}
            {selectedMetrics.includes('demosCpd') && <TableCell align="right">Demos</TableCell>}
            {selectedMetrics.includes('demosCpd') && <TableCell align="right">Cost/Demo</TableCell>}
            {selectedMetrics.includes('soldCps') && <TableCell align="right">Sold</TableCell>}
            {selectedMetrics.includes('soldCps') && <TableCell align="right">Cost/Sale</TableCell>}
            {selectedMetrics.includes('revenueCom') && <TableCell align="right">Revenue</TableCell>}
            {selectedMetrics.includes('revenueCom') && <TableCell align="right">CoM</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const thumb = pickMedia(row);
            const ageDays = getAdAgeDays(row.createdTime);
            const maturing = ageDays != null && ageDays < MATURING_DAYS;
            const maturityStage = getMaturityStage(row);
            const comConfidence = getComConfidence(row);
            return (
              <TableRow
                key={row.uuid}
                hover
                onClick={() => onRowClick(row)}
                sx={{ cursor: 'pointer', opacity: row.isActive ? 1 : 0.65 }}
              >
                <TableCell>
                  {thumb ? (
                    <Box
                      component="img"
                      src={thumb}
                      alt={row.adName}
                      sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                    />
                  ) : (
                    <Box sx={{ width: 56, height: 56, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }} />
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                    {row.vertical ? (
                      <Chip
                        label={row.vertical}
                        size="small"
                        color="primary"
                        sx={{ height: 18, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                      />
                    ) : null}
                    {maturing ? (
                      <Tooltip title={maturingReason(ageDays)}>
                        <Chip
                          label="🌱 Maturing"
                          size="small"
                          color="warning"
                          sx={{ height: 18, fontSize: 11, fontWeight: 700, bgcolor: 'warning.main', color: '#fff' }}
                        />
                      </Tooltip>
                    ) : null}
                    <ConfidenceChip chip={maturityStage} height={18} />
                    <ConfidenceChip chip={comConfidence} height={18} />
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.adName || '(no name)'}
                  </Typography>
                  {row.headline ? (
                    <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                      {row.headline}
                    </Typography>
                  ) : null}
                  <CopyableAdId adId={row.adId} sx={{ mt: 0.25 }} />
                  {row.createdTime ? (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: maturing ? 'warning.main' : 'text.secondary', fontWeight: maturing ? 700 : 400 }}
                    >
                      Launched {formatLaunchDate(row.createdTime)} · {formatAdAge(ageDays)}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{row.clientName || '—'}</TableCell>
                <TableCell><StatusPill active={row.isActive} /></TableCell>
                {selectedMetrics.includes('spend') && <TableCell align="right"><DollarFormatter value={row.spend} /></TableCell>}
                {selectedMetrics.includes('leadsCpl') && <TableCell align="right">{row.leads.toLocaleString()}</TableCell>}
                {selectedMetrics.includes('leadsCpl') && <TableCell align="right">{row.cpl > 0 ? <DollarFormatter value={row.cpl} /> : '—'}</TableCell>}
                {selectedMetrics.includes('bookedCpb') && <TableCell align="right">{row.booked.toLocaleString()}</TableCell>}
                {selectedMetrics.includes('bookedCpb') && <TableCell align="right">{row.costPerBooked > 0 ? <DollarFormatter value={row.costPerBooked} /> : '—'}</TableCell>}
                {selectedMetrics.includes('issuesCpi') && <TableCell align="right">{row.issues.toLocaleString()}</TableCell>}
                {selectedMetrics.includes('issuesCpi') && <TableCell align="right">{row.costPerIssue > 0 ? <DollarFormatter value={row.costPerIssue} /> : '—'}</TableCell>}
                {selectedMetrics.includes('demosCpd') && <TableCell align="right">{row.demos.toLocaleString()}</TableCell>}
                {selectedMetrics.includes('demosCpd') && <TableCell align="right">{row.costPerDemo > 0 ? <DollarFormatter value={row.costPerDemo} /> : '—'}</TableCell>}
                {selectedMetrics.includes('soldCps') && <TableCell align="right">{row.netSold.toLocaleString()}</TableCell>}
                {selectedMetrics.includes('soldCps') && <TableCell align="right">{row.costPerSale > 0 ? <DollarFormatter value={row.costPerSale} /> : '—'}</TableCell>}
                {selectedMetrics.includes('revenueCom') && <TableCell align="right">{row.netAmount > 0 ? <DollarFormatter value={row.netAmount} /> : '—'}</TableCell>}
                {selectedMetrics.includes('revenueCom') && <TableCell align="right">{row.netAmount > 0 ? <PercentageFormatter value={row.costOfMarketing} /> : '—'}</TableCell>}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AdPreviewModal({ row, onClose }) {
  const [iframeHtml, setIframeHtml] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!row) return undefined;
    let cancelled = false;
    setIframeHtml('');
    setError(null);
    setLoading(true);
    fetchAdPreview({ ad_id: row.adId, ad_format: 'MOBILE_FEED_STANDARD' })
      .then((response) => {
        if (cancelled) return;
        const data = response?.data || {};
        setIframeHtml(data.iframe_html || '');
        if (!data.iframe_html) setError('Facebook returned an empty preview.');
      })
      .catch(() => {
        if (cancelled) return;
        setError('Unable to load Facebook preview.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [row]);

  if (!row) return null;

  const ageDays = getAdAgeDays(row.createdTime);
  const maturing = ageDays != null && ageDays < MATURING_DAYS;
  const dimReason = maturing ? maturingReason(ageDays) : '';
  const maturityStage = getMaturityStage(row);
  const comConfidence = getComConfidence(row);
  // Maturing ad with leads old enough to judge → show the honest mature
  // read. Maturing ad with no mature leads yet → nothing trustworthy to
  // show, fall back to dimming.
  const showMature = maturing && row.hasMatureData;
  const dimBackend = maturing && !row.hasMatureData;

  return (
    <Dialog open={Boolean(row)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.adName || 'Ad Preview'}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            {row.vertical ? (
              <Chip
                label={row.vertical}
                size="small"
                color="primary"
                sx={{ height: 20, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}
              />
            ) : null}
            {maturing ? (
              <Chip
                label="🌱 Maturing"
                size="small"
                color="warning"
                sx={{ height: 20, fontWeight: 700, bgcolor: 'warning.main', color: '#fff' }}
              />
            ) : null}
            <ConfidenceChip chip={maturityStage} height={20} />
            <ConfidenceChip chip={comConfidence} height={20} />
            {row.clientName ? (
              <Typography variant="caption" color="text.secondary">{row.clientName}</Typography>
            ) : null}
            <CopyableAdId adId={row.adId} />
            {row.createdTime ? (
              <Typography variant="caption" color="text.secondary">
                Launched {formatLaunchDate(row.createdTime)} · {formatAdAge(ageDays)}
              </Typography>
            ) : null}
          </Stack>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
          <Box sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0 }}>
            {loading ? (
              <Box sx={{ minHeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
                <CircularProgress size={32} />
              </Box>
            ) : error ? (
              <Box sx={{ minHeight: 200, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
                <Alert severity="warning">{error}</Alert>
              </Box>
            ) : (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'grey.50',
                  '& iframe': { width: '100%', minHeight: 600, border: 0, display: 'block' },
                }}
                dangerouslySetInnerHTML={{ __html: iframeHtml }}
              />
            )}
          </Box>
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}><StatusPill active={row.isActive} /></Box>
            </Box>
            {maturing ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {showMature
                  ? `Launched ${formatAdAge(ageDays)}. Booking, demo and sale metrics below show the mature read — leads old enough (7+ days) to move through the funnel. The grey "all leads" line includes recent leads still in the 7–10 day pipeline, so it reads worse than reality.`
                  : `Launched ${formatAdAge(ageDays)}. No leads from this ad are old enough to have converted yet, so booking, demo and sale metrics won't be meaningful for a few more days. Spend and leads are real.`}
              </Alert>
            ) : null}
            <Box>
              <Typography variant="overline" color="text.secondary">
                {showMature ? 'Performance · mature read' : 'Performance'}
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(2, 1fr)', mt: 0.5 }}>
                <MetricCell label="Spend" value={<DollarFormatter value={row.spend} />} />
                <MetricCell label="Leads" value={row.leads.toLocaleString()} sub={row.cpl > 0 ? <DollarFormatter value={row.cpl} /> : '—'} subLabel="CPL" />
                <MetricCell
                  label="Booked"
                  value={(showMature ? row.matureBooked : row.booked).toLocaleString()}
                  sub={(showMature ? row.matureCostPerBooked : row.costPerBooked) > 0
                    ? <DollarFormatter value={showMature ? row.matureCostPerBooked : row.costPerBooked} />
                    : '—'}
                  subLabel="Cost/Booked"
                  dimmed={dimBackend}
                  dimReason={dimReason}
                  note={showMature ? `All leads: ${row.booked.toLocaleString()}` : null}
                />
                <MetricCell
                  label="Issues"
                  value={(showMature ? row.matureIssues : row.issues).toLocaleString()}
                  sub={(showMature ? row.matureCostPerIssue : row.costPerIssue) > 0
                    ? <DollarFormatter value={showMature ? row.matureCostPerIssue : row.costPerIssue} />
                    : '—'}
                  subLabel="Cost/Issue"
                  dimmed={dimBackend}
                  dimReason={dimReason}
                  note={showMature ? `All leads: ${row.issues.toLocaleString()}` : null}
                />
                <MetricCell
                  label="Demos"
                  value={(showMature ? row.matureDemos : row.demos).toLocaleString()}
                  sub={(showMature ? row.matureCostPerDemo : row.costPerDemo) > 0
                    ? <DollarFormatter value={showMature ? row.matureCostPerDemo : row.costPerDemo} />
                    : '—'}
                  subLabel="Cost/Demo"
                  dimmed={dimBackend}
                  dimReason={dimReason}
                  note={showMature ? `All leads: ${row.demos.toLocaleString()}` : null}
                />
                <MetricCell
                  label="Sold"
                  value={(showMature ? row.matureNetSold : row.netSold).toLocaleString()}
                  sub={(showMature ? row.matureCostPerSale : row.costPerSale) > 0
                    ? <DollarFormatter value={showMature ? row.matureCostPerSale : row.costPerSale} />
                    : '—'}
                  subLabel="Cost/Sale"
                  dimmed={dimBackend}
                  dimReason={dimReason}
                  note={showMature ? `All leads: ${row.netSold.toLocaleString()}` : null}
                />
                <MetricCell
                  label="Revenue"
                  value={(showMature ? row.matureNetAmount : row.netAmount) > 0
                    ? <DollarFormatter value={showMature ? row.matureNetAmount : row.netAmount} />
                    : '—'}
                  sub={(showMature ? row.matureNetAmount : row.netAmount) > 0
                    ? <PercentageFormatter value={showMature ? row.matureCostOfMarketing : row.costOfMarketing} />
                    : '—'}
                  subLabel="CoM"
                  dimmed={dimBackend}
                  dimReason={dimReason}
                  note={showMature && row.netAmount > 0
                    ? <>All leads CoM: <PercentageFormatter value={row.costOfMarketing} /></>
                    : null}
                />
              </Box>
            </Box>
            {row.headline ? (
              <Box>
                <Typography variant="overline" color="text.secondary">Headline</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{row.headline}</Typography>
              </Box>
            ) : null}
            {row.primaryText ? (
              <Box>
                <Typography variant="overline" color="text.secondary">Primary text</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.primaryText}</Typography>
              </Box>
            ) : null}
            {row.description ? (
              <Box>
                <Typography variant="overline" color="text.secondary">Description</Typography>
                <Typography variant="body2">{row.description}</Typography>
              </Box>
            ) : null}
            <Stack direction="row" spacing={2}>
              {row.ctaType ? (
                <Box>
                  <Typography variant="overline" color="text.secondary">CTA</Typography>
                  <Typography variant="body2">{row.ctaType}</Typography>
                </Box>
              ) : null}
              {row.destinationUrl ? (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary">Destination</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{row.destinationUrl}</Typography>
                </Box>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function readStoredArray(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : fallback;
  } catch (err) {
    return fallback;
  }
}

function readStoredString(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch (err) {
    return fallback;
  }
}

function writeStored(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch (err) {
    // ignore
  }
}

export default function AdPerformancePage() {
  const { state: authState } = useAuth();
  const user = authState?.user;
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const clientMemberships = Array.isArray(user?.client_memberships) ? user.client_memberships : [];
  // Parent-company contacts (membership scoped to a parent_company, no single
  // client_id) and people on more than one client need a client picker so they
  // can scope to a single brand. Staff always get the full picker. A
  // single-client member has nothing to pick, so we skip the extra metadata
  // load for them and the backend simply scopes to their one client.
  const isParentMember = clientMemberships.some(
    (m) => m?.parent_company_id && !m?.client_id,
  );
  const needsClientScope = isStaff || isParentMember || clientMemberships.length > 1;

  const [defaultStart, defaultEnd] = React.useMemo(() => getLastThirtyDays(), []);
  const [startDate, setStartDate] = React.useState(defaultStart);
  const [endDate, setEndDate] = React.useState(defaultEnd);
  const [loading, setLoading] = React.useState(true);
  const [loadingClientMeta, setLoadingClientMeta] = React.useState(needsClientScope);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [clientMetadata, setClientMetadata] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [selectedVerticals, setSelectedVerticals] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState('active');
  const [viewMode, setViewMode] = React.useState(() => readStoredString(VIEW_STORAGE_KEY, 'tiles'));
  const [selectedMetrics, setSelectedMetrics] = React.useState(() => readStoredArray(METRICS_STORAGE_KEY, DEFAULT_SELECTED_METRICS));
  const [previewRow, setPreviewRow] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [showDisclaimerDetails, setShowDisclaimerDetails] = React.useState(false);

  React.useEffect(() => { writeStored(VIEW_STORAGE_KEY, viewMode); }, [viewMode]);
  React.useEffect(() => { writeStored(METRICS_STORAGE_KEY, selectedMetrics); }, [selectedMetrics]);

  React.useEffect(() => {
    let isMounted = true;
    if (!needsClientScope) {
      setClientMetadata([]);
      setLoadingClientMeta(false);
      return () => { isMounted = false; };
    }
    const loadClients = async () => {
      setLoadingClientMeta(true);
      try {
        const clients = await fetchAllClientMetadata();
        if (!isMounted) return;
        setClientMetadata(clients.map((client = {}) => ({
          id: String(client.id),
          name: client.name || '',
          parentCompanyId: client.parent_company ? String(client.parent_company) : '__none__',
          parentCompanyName: client.parent_company_name || 'No Parent Company',
          hasMapping: Array.isArray(client.mapping_types) && client.mapping_types.length > 0,
        })));
      } catch (err) {
        if (isMounted) { setClientMetadata([]); setError('Unable to load client metadata.'); }
      } finally {
        if (isMounted) setLoadingClientMeta(false);
      }
    };
    loadClients();
    return () => { isMounted = false; };
  }, [needsClientScope]);

  const clientMetaById = React.useMemo(() => {
    const next = new Map();
    if (clientMetadata.length) {
      clientMetadata.forEach((c) => next.set(c.id, c));
    } else {
      // Single-client members: no metadata fetch, so map names straight from
      // their membership(s).
      clientMemberships.forEach((m) => {
        if (m?.client_id) {
          next.set(String(m.client_id), {
            id: String(m.client_id),
            name: m.client_name || '',
          });
        }
      });
    }
    return next;
  }, [clientMetadata, clientMemberships]);

  const clientOptions = React.useMemo(
    () => clientMetadata.map((c) => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [clientMetadata]
  );

  React.useEffect(() => {
    if (!needsClientScope) return;
    const allowed = new Set(clientOptions.map((o) => o.id));
    setSelectedClients((prev) => {
      const next = prev.filter((id) => allowed.has(id));
      return arraysEqual(prev, next) ? prev : next;
    });
  }, [clientOptions, needsClientScope]);

  const effectiveClientIds = React.useMemo(() => {
    if (!needsClientScope) return [];
    if (selectedClients.length > 0) return selectedClients;
    // Staff with no selection query every client; non-staff with no selection
    // send no client_ids and let the backend scope to their own clients
    // (a parent contact's children, etc.).
    return isStaff ? clientMetadata.map((c) => c.id) : [];
  }, [clientMetadata, isStaff, needsClientScope, selectedClients]);

  // Vertical options are derived from the loaded ads so they always
  // reflect what's actually in the result set.
  const verticalOptions = React.useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => { if (r.vertical) seen.add(r.vertical); });
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  React.useEffect(() => {
    const allowed = new Set(verticalOptions);
    setSelectedVerticals((prev) => {
      const next = prev.filter((v) => allowed.has(v));
      return arraysEqual(prev, next) ? prev : next;
    });
  }, [verticalOptions]);

  const filteredRows = React.useMemo(() => {
    if (selectedVerticals.length === 0) return rows;
    const wanted = new Set(selectedVerticals);
    return rows.filter((r) => wanted.has(r.vertical));
  }, [rows, selectedVerticals]);

  React.useEffect(() => {
    let isMounted = true;
    if (loadingClientMeta) return undefined;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ordering: '-total_spend_sum',
          // effectiveClientIds is empty for non-staff with no explicit pick, so
          // the backend scopes to their own clients; a parent contact who picks
          // one brand sends just that client_id (still inside their scope).
          ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
          ...(statusFilter === 'active' ? { active: 'true' } : {}),
        };
        const response = await fetchAdSummaryByAd(payload);
        const data = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.results) ? response.data.results : [];
        if (!isMounted) return;
        const mapped = data.map((row) => mapRow(row, clientMetaById));
        // Creative Performance is a broad-campaign view: local/county
        // campaigns add hundreds of low-spend ads that drown out the
        // strategic creatives we actually use this page to compare.
        // Backend still pulls everything for other reports.
        const filtered = mapped.filter((r) => {
          const t = (r.campaignType || '').toLowerCase();
          return t !== 'local' && t !== 'county';
        });
        filtered.sort((a, b) => b.spend - a.spend);
        setRows(filtered);
      } catch (err) {
        if (isMounted) { setRows([]); setError('Unable to load creative performance data.'); }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [clientMetaById, effectiveClientIds, endDate, isStaff, loadingClientMeta, startDate, statusFilter, refreshTick]);

  const totals = React.useMemo(() => {
    const base = filteredRows.reduce((acc, row) => {
      acc.spend += row.spend;
      acc.impressions += row.impressions;
      acc.clicks += row.clicks;
      acc.landingPageViews += row.landingPageViews;
      acc.videoPlays3s += row.videoPlays3s;
      acc.thruplays += row.thruplays;
      acc.leads += row.leads;
      acc.booked += row.booked;
      acc.issues += row.issues;
      acc.demos += row.demos;
      acc.netSold += row.netSold;
      acc.netAmount += row.netAmount;
      return acc;
    }, { spend: 0, impressions: 0, clicks: 0, landingPageViews: 0, videoPlays3s: 0, thruplays: 0, leads: 0, booked: 0, issues: 0, demos: 0, netSold: 0, netAmount: 0 });
    return { ...base, ...computeDerivedMetrics(base) };
  }, [filteredRows]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedRows = React.useMemo(
    () => filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredRows, safePage]
  );

  // Reset to the first page whenever the result set or layout changes so
  // the user never lands on a now-empty page.
  React.useEffect(() => { setPage(1); }, [filteredRows, viewMode]);

  const handleDateRangeChange = (event) => {
    const selection = event?.selection || {};
    if (selection.startDate) setStartDate(selection.startDate);
    if (selection.endDate) setEndDate(selection.endDate);
  };

  const toggleMetric = (key) => {
    setSelectedMetrics((prev) => {
      const isOn = prev.includes(key);
      if (isOn) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  return (
    <Page title="Creative Performance" fullWidth>
      <Stack spacing={3}>
        {!isStaff && (
          <Alert severity="info">
            <strong>Welcome to Creative Performance — your behind-the-scenes look at the ads we're running for you.</strong>{' '}
            It's in beta, so we're still polishing the numbers. Here's a little
            context to help you read it.
            <Box sx={{ mt: 0.5 }}>
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => setShowDisclaimerDetails((prev) => !prev)}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: 'inherit', fontWeight: 600 }}
              >
                {showDisclaimerDetails ? 'Hide details' : 'Read the details'}
                {showDisclaimerDetails
                  ? <ExpandLessIcon fontSize="small" />
                  : <ExpandMoreIcon fontSize="small" />}
              </Link>
            </Box>
            <Collapse in={showDisclaimerDetails}>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                <li>
                  <strong>Your team's got the wheel.</strong> We're actively managing
                  the pausing, scaling, and budget calls behind the scenes — so you
                  can enjoy the view here without anything you need to act on.
                </li>
                <li>
                  <strong>Some numbers may read a little low.</strong> We match
                  performance to each ad using tracking tags (UTMs), and when one is
                  missing or a result comes in late, a real lead or sale might not get
                  credited to the ad that earned it. We're actively tightening this up
                  during beta.
                </li>
                <li>
                  <strong>A lower number doesn't mean a weak ad.</strong> People
                  usually see several of your ads before they convert, so an ad that
                  rarely gets the final click can still be doing important work earlier
                  in the journey. We look at the whole funnel — not just last-click —
                  when we decide what to run.
                </li>
              </Box>
            </Collapse>
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
                <Box sx={{ minWidth: 280, maxWidth: { lg: 320 } }}>
                  <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} style={{ width: '100%' }} />
                </Box>
                {(isStaff || clientOptions.length > 1) ? (
                  <Autocomplete
                    multiple
                    size="small"
                    sx={{ minWidth: 220, flex: 1 }}
                    options={clientOptions.map((o) => ({ label: o.name, value: o.id }))}
                    value={clientOptions.filter((o) => selectedClients.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))}
                    onChange={(_, v) => setSelectedClients(v.map((o) => o.value))}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    getOptionLabel={(o) => o.label || ''}
                    renderInput={(params) => <TextField {...params} label="Clients" />}
                  />
                ) : null}
                {verticalOptions.length > 0 ? (
                  <Autocomplete
                    multiple
                    size="small"
                    sx={{ minWidth: 200, flex: 1 }}
                    options={verticalOptions}
                    value={selectedVerticals}
                    onChange={(_, v) => setSelectedVerticals(v)}
                    getOptionLabel={(o) => o || ''}
                    renderInput={(params) => <TextField {...params} label="Verticals" />}
                  />
                ) : null}
                {isStaff && (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={statusFilter}
                    onChange={(_, v) => { if (v) setStatusFilter(v); }}
                    sx={{ flexShrink: 0 }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={viewMode}
                  onChange={(_, v) => { if (v) setViewMode(v); }}
                  sx={{ flexShrink: 0 }}
                >
                  <ToggleButton value="tiles" aria-label="Tile view">
                    <Tooltip title="Tile view"><GridViewIcon fontSize="small" /></Tooltip>
                  </ToggleButton>
                  <ToggleButton value="table" aria-label="Table view">
                    <Tooltip title="Table view"><ViewListIcon fontSize="small" /></Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title="Refresh data">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setRefreshTick((t) => t + 1)}
                      disabled={loading || loadingClientMeta}
                      aria-label="Refresh"
                      sx={{ flexShrink: 0 }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Show metrics:</Typography>
                {METRIC_OPTIONS.map((opt) => {
                  const active = selectedMetrics.includes(opt.key);
                  return (
                    <Chip
                      key={opt.key}
                      label={opt.label}
                      size="small"
                      color={active ? 'primary' : 'default'}
                      variant={active ? 'filled' : 'outlined'}
                      onClick={() => toggleMetric(opt.key)}
                    />
                  );
                })}
              </Stack>
              <Stack direction="row" spacing={2.5} sx={{ flexWrap: 'wrap', rowGap: 1, pt: 0.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Spend</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}><DollarFormatter value={totals.spend} /></Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Leads</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{totals.leads.toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Booked</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{totals.booked.toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Sold</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{totals.netSold.toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Revenue</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}><DollarFormatter value={totals.netAmount} /></Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">CoM</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {totals.netAmount > 0 ? <PercentageFormatter value={totals.costOfMarketing} /> : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Ads</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{filteredRows.length}</Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {loading || loadingClientMeta ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : filteredRows.length === 0 ? (
          <Card><CardContent>
            <Typography variant="body2" color="text.secondary" align="center">
              {rows.length > 0
                ? 'No ads match the selected verticals.'
                : isStaff
                  ? 'No ads in this range.'
                  : 'No active ads for this date range yet. As your campaigns run, the ads we launch for you will show up here.'}
            </Typography>
          </CardContent></Card>
        ) : (
          <Stack spacing={2}>
            {viewMode === 'tiles' ? (
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                }}
              >
                {pagedRows.map((row) => (
                  <AdTile key={row.uuid} row={row} selectedMetrics={selectedMetrics} onClick={setPreviewRow} />
                ))}
              </Box>
            ) : (
              <Card><CardContent sx={{ p: 0 }}>
                <AdTable rows={pagedRows} selectedMetrics={selectedMetrics} onRowClick={setPreviewRow} />
              </CardContent></Card>
            )}
            {pageCount > 1 ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {`Showing ${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, filteredRows.length)} of ${filteredRows.length} ads`}
                </Typography>
                <Pagination
                  count={pageCount}
                  page={safePage}
                  onChange={(_, value) => setPage(value)}
                  size="small"
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            ) : null}
          </Stack>
        )}

        <AdPreviewModal row={previewRow} onClose={() => setPreviewRow(null)} />
      </Stack>
    </Page>
  );
}
